import Button from '@/shared/Buttons/Button'
import DropdownInput from '@/shared/Inputs/DropdownInput'
import Form from '@/shared/Forms/Form'
import FormField from '@/shared/Forms/FormField'
import SidePage from '@/shared/SidePage'
import TextArea from '@/shared/Inputs/TextArea'
import TextInput from '@/shared/Inputs/TextInput'
import Toggle from '@/shared/Inputs/Toggle'
import AssetPicker from './AssetPicker'
import MarkdownEditor from './MarkdownEditor'
import { CmsEditorState } from '../hooks/useCmsResource'
import { CmsEntity, CmsFormValue, CmsLookupContext, CmsResourceConfig, ICmsAsset } from '../cms.types'
import css from '../cms.module.css'

interface IProps<T extends CmsEntity & Record<string, any>> {
  resource: CmsResourceConfig<T>
  editor: CmsEditorState<T> | null
  context: CmsLookupContext
  assetsLoading: boolean
  saving: boolean
  onChange: (value: CmsFormValue, name: string) => void
  onClose: () => void
  onSave: () => Promise<boolean>
  onAssetsRefresh: () => Promise<void>
}

const CmsEntityForm = <T extends CmsEntity & Record<string, any>>(props: IProps<T>) => {
  const editor = props.editor

  const renderField = (field: CmsResourceConfig<T>['fields'][number]) => {
    if (!editor) return null

    const value = editor.form[field.name]
    const disabled = editor.mode === 'edit' && field.readonlyOnEdit
    const error = Boolean(editor.errors[field.name])

    if (field.kind === 'textarea') {
      return (
        <TextArea
          name={field.name}
          value={String(value ?? '')}
          onChange={props.onChange}
          placeHolder={field.placeholder}
          disabled={disabled}
          error={error}
          inscription={editor.errors[field.name]}
        />
      )
    }

    if (field.kind === 'markdown') {
      return (
        <MarkdownEditor
          name={field.name}
          value={String(value ?? '')}
          onChange={props.onChange}
          placeholder={field.placeholder}
          disabled={disabled}
          error={error}
          inscription={editor.errors[field.name]}
        />
      )
    }

    if (field.kind === 'number') {
      return (
        <TextInput
          name={field.name}
          value={String(value ?? 0)}
          onChange={props.onChange}
          placeHolder={field.placeholder}
          disabled={disabled}
          error={error}
          inscription={editor.errors[field.name]}
          type="number"
        />
      )
    }

    if (field.kind === 'select') {
      const options = field.getOptions ? field.getOptions(props.context) : field.options || []

      return (
        <DropdownInput
          options={options}
          selectedOption={value}
          name={field.name}
          title="Выберите значение"
          onSelect={props.onChange}
          disabled={disabled}
        />
      )
    }

    if (field.kind === 'boolean') {
      return (
        <div className={css.booleanField}>
          <Toggle
            name={field.name}
            checked={Boolean(value)}
            onChange={props.onChange}
            size="large"
            editing={!disabled}
          />
          <span className="text-nm text-description">{Boolean(value) ? 'Да' : 'Нет'}</span>
        </div>
      )
    }

    if (field.kind === 'asset') {
      return (
        <AssetPicker
          name={field.name}
          value={String(value ?? '')}
          assets={props.context.assets as ICmsAsset[]}
          loading={props.assetsLoading}
          error={error}
          inscription={editor.errors[field.name]}
          onChange={props.onChange}
          onRefresh={props.onAssetsRefresh}
        />
      )
    }

    return (
      <TextInput
        name={field.name}
        value={String(value ?? '')}
        onChange={props.onChange}
        placeHolder={field.placeholder}
        disabled={disabled}
        error={error}
        inscription={editor.errors[field.name]}
      />
    )
  }

  return (
    <SidePage open={Boolean(editor)} onClose={props.onClose} size="default">
      {editor && (
        <div className={css.sidePageContent}>
          <h2 className="text-heading-4">
            {editor.mode === 'create' ? 'Создать' : 'Редактировать'} {props.resource.singularTitle}
          </h2>
          <Form className={css.formGrid} onSubmit={() => props.onSave()}>
            {props.resource.fields.map((field) => (
              <FormField key={field.name} label={field.label} required={field.required}>
                {renderField(field)}
              </FormField>
            ))}
            <div className={css.formFooter}>
              <Button size="default" type="default" text="Отмена" onClick={props.onClose} disabled={props.saving} />
              <Button
                size="default"
                type="primary"
                text="Сохранить"
                buttonType="submit"
                loading={props.saving}
              />
            </div>
          </Form>
        </div>
      )}
    </SidePage>
  )
}

export default CmsEntityForm
